import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import {
    GridColDefPro,
    GridRenderCellParams,
} from '@/global/components/DataGrid/utils/types'
import { JornadaDiaria, JornadaMensal } from '@/service/api'
import { SN, StatusAprovacao } from '@acme/core'
import React, { useMemo } from 'react'
import { ExibirDia } from './ExibirDia'
import Ausencias from './ausencias'
import FaltasCell from './faltas'
import { OpcoesDigitacao, useOpcoesDigitacaoHooks } from './opcoes'
import { useDigitacaoContext } from './pages/useDigitacaoContext'
import RegistrosCell from './registros-cell'
import { SaldoDiarioCell } from './saldo-diario'

function valueGetterAusencias({
    row: {
        afastamento,
        compensacao,
        compensacaoManual,
        abono,
        descontoDsr,
        folga,
        trocaDiaTrabalho,
        feriado,
        faltaCadastrada,
    },
}: {
    row: JornadaDiaria | JornadaMensal
}) {
    return {
        afastamento:
            !!afastamento &&
            afastamento.statusAprovacao !== StatusAprovacao.REPROVADO
                ? afastamento
                : undefined,
        compensacao: !!compensacao && !feriado ? compensacao : undefined,
        compensacaoManual: compensacaoManual ? compensacaoManual : undefined,
        abono:
            !!abono && abono.statusAprovacao !== StatusAprovacao.REPROVADO
                ? abono
                : undefined,
        descontoDsr,
        folga,
        trocaDiaTrabalho:
            trocaDiaTrabalho && !folga ? trocaDiaTrabalho : undefined,
        faltaCadastrada: faltaCadastrada ? faltaCadastrada : undefined,
    }
}

const MemoziedRegistrosCell = React.memo(
    RegistrosCell,
    (prevProps, nextProps) => {
        const { row: prevRow } = prevProps as {
            row: JornadaDiaria | JornadaMensal
        }
        const { row: nextRow } = nextProps as {
            row: JornadaDiaria | JornadaMensal
        }

        return (
            prevRow.registros.map(r => String(r.updatedAt)).join(',') ===
                nextRow.registros.map(r => String(r.updatedAt)).join(',') &&
            prevRow.dia === nextRow.dia
        )
    },
)

function mensalColumns(
    /** empresa?.exibirSaldoDiario */
    exibirSaldoDiario: boolean,
    /** Componente renderizado no botão opções,
     * controlado por memo
     */
    Opcoes: (params: GridRenderCellParams) => React.ReactNode,
): GridColDefPro[] {
    return [
        {
            field: 'dia',
            headerName: 'Dia',
            width: 55,
            renderCell: ExibirDia,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: 'registros',
            headerName: 'Registros',
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'center',
            renderCell: props => <MemoziedRegistrosCell {...props} />,
        },
        {
            field: 'ausencias',
            headerName: '',
            minWidth: 200,
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: valueGetterAusencias,
            renderCell: Ausencias,
        },
        {
            field: 'horasTrabalhadas',
            headerName: 'Total',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        ...(exibirSaldoDiario
            ? [
                  {
                      field: 'saldoDiario',
                      headerName: 'Saldo',
                      width: 60,
                      filterable: false,
                      disableColumnMenu: true,
                      sortable: false,
                      align: 'center',
                      headerAlign: 'center',
                      renderCell: SaldoDiarioCell,
                  },
              ]
            : []),
        {
            field: 'faltas',
            headerName: 'Faltas',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: FaltasCell,
        },
        {
            field: 'horasExtra',
            headerName: 'H. Extras',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        {
            field: 'sobreaviso',
            headerName: 'Sobreaviso',
            width: 90,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value?.total || '00:00',
        },
        {
            ...optionsColumn,
            field: 'opcoes',
            renderCell: Opcoes,
        },
    ] as GridColDefPro[]
}

export function diarioColumns(
    Opcoes: (params: GridRenderCellParams) => React.ReactNode,
): GridColDefPro[] {
    return [
        {
            field: 'nome',
            headerName: 'Funcionário',
            width: 220,
        },
        {
            field: 'registros',
            headerName: 'Registros',
            minWidth: 810,
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'center',
            renderCell: RegistrosCell,
        },
        {
            field: 'ausencias',
            headerName: '',
            width: 200,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: valueGetterAusencias,
            renderCell: Ausencias,
        },
        {
            field: 'horasTrabalhadas',
            headerName: 'Total',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        {
            field: 'faltas',
            headerName: 'Faltas',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        {
            field: 'horasExtra',
            headerName: 'H. Extras',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value || '00:00',
        },
        {
            field: 'sobreaviso',
            headerName: 'Sobreaviso',
            width: 90,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: ({ value }) => value?.total || '00:00',
        },
        {
            ...optionsColumn,
            field: 'opcoes',
            renderCell: Opcoes,
        },
    ]
}

export function useDigitacaoColumns() {
    const { view } = useDigitacaoContext()
    const hooks = useOpcoesDigitacaoHooks()

    const keys =
        view === 'mensal'
            ? ([
                  hooks.funcionario?.id,
                  hooks.funcionario?.ajustesPonto,
                  hooks.empresa?.id,
                  hooks.empresa?.exibirSaldoDiario,
                  hooks.categoria,
                  view,
              ] as const)
            : ([
                  hooks.empresa?.id,
                  hooks.empresa?.exibirSaldoDiario,
                  view,
              ] as const)

    const columns = useMemo(
        () =>
            view === 'mensal'
                ? mensalColumns(
                      SN.isTrue(hooks.empresa?.exibirSaldoDiario),
                      params => <OpcoesDigitacao {...params} {...hooks} />,
                  )
                : diarioColumns(params => (
                      <OpcoesDigitacao {...params} {...hooks} />
                  )),
        keys,
    )

    return { columns, colsHash: keys.join('-') }
}
